import Phaser from 'phaser';
import { GameMode } from './GameMode.js';

export class FreeForAllMode extends GameMode {
  constructor() {
    super('Free-for-All', 'Everyone fights everyone - last player standing wins');
  }

  initialize(scene) {
    // No special initialization needed for free-for-all
    console.log('[GameMode] Initialized Free-for-All mode');
  }

  spawnPlayers(scene, players) {
    // Use existing random spawn logic
    //const worldBounds = scene.gameManager?.worldBounds || { x: 0, y: 0, width: 2048, height: 1152 };
    const spawnBounds = scene.gameManager?.spawnBoundsInitial || scene.gameManager?.worldBounds || { x: 0, y: 0, width: 2048, height: 1152 };
    
    players.forEach(player => {
      // Random spawn position within world bounds
      const x = Phaser.Math.Between(spawnBounds.x + 50, spawnBounds.x + spawnBounds.width - 50);
      const y = Phaser.Math.Between(spawnBounds.y + 50, spawnBounds.y + spawnBounds.height - 50);
      
      // Set sprite position properly
      player.sprite.setPosition(x, y);
      player.targetX = x;
      player.targetY = y;
      
      // Update UI elements position
      if (player.nameText) {
        player.nameText.setPosition(x, y - 43 * scene.objectScale);
      }
      if (player.healthBarBg) {
        player.healthBarBg.setPosition(x, y - 33 * scene.objectScale);
      }
      if (player.healthBar) {
        player.healthBar.setPosition(x, y - 33 * scene.objectScale);
      }
      if (player.shieldSprite) {
        player.shieldSprite.setPosition(x, y - 50 * scene.objectScale);
      }
      
      // No team assignment for free-for-all
      player.team = null;
      player.teamColor = null;
    });
    
    console.log(`[GameMode] Spawned ${players.length} players in Free-for-All mode`);
  }

  checkCombat(scene) {
    // Use existing all-vs-all combat logic
    scene.players.forEach(p => { p.prevBattling = p.isBattling; p.isBattling = false; });
    
    for (let i = 0; i < scene.players.length; i++) {
      for (let j = i + 1; j < scene.players.length; j++) {
        const p1 = scene.players[i];
        const p2 = scene.players[j];
        if (p1.isAlive && p2.isAlive) {
          const dist = Phaser.Math.Distance.Between(p1.sprite.x, p1.sprite.y, p2.sprite.x, p2.sprite.y);
          if (dist < scene.meleeRange) {
            p1.isBattling = true;
            p2.isBattling = true;
            // Combat!
            const now = scene.time.now;
            const damage = p1.calculateMeleeDamage();
            if (now > p1.lastAttackTime + scene.getAttackCooldownMs(p1)) {
              p1.lastAttackTime = now;
              if (typeof p1.applyAttackStaminaCost === 'function') {
                p1.applyAttackStaminaCost(2);
              }
              // Clear charging flag after first attack
              if (p1.isCharging) p1.isCharging = false;
              const willDie = p2.health <= damage;
              const actualDamage = p2.takeDamage(damage, { critical: !!p1.lastAttackWasCritical });
              p1.damageDealt += actualDamage;
              if (typeof p1.handleDamageDealtForUltimate === 'function') {
                p1.handleDamageDealtForUltimate(actualDamage);
              }
              scene.createAttackEffect(p1, p2);
              if (scene.sfxManager) {
                scene.sfxManager.onAttack(p1);
                scene.sfxManager.onHit(p2, actualDamage, willDie);
              }
              // Taunt triggers
              if (scene.tauntManager) {
                scene.tauntManager.triggerTaunt(p1, 'attack');
                scene.tauntManager.triggerTaunt(p1, 'hit');
                scene.tauntManager.triggerTaunt(p2, 'hurt');
              }
              if (!p2.isAlive) {
                //console.log('💀 Player killed:', p2.name, 'by', p1.name, '- Remaining alive:', scene.players.filter(p => p.isAlive).length - 1);
                p1.addElimination();
                scene.createDeathMarker(p2.sprite.x, p2.sprite.y, { popScale: p2.sprite.scale * 10 });
                if (scene.sfxManager) scene.sfxManager.onDeath(p2);
                // Taunt triggers for kill/death
                if (scene.tauntManager) {
                  scene.tauntManager.triggerTaunt(p1, 'killed');
                  scene.tauntManager.triggerTaunt(p2, 'death');
                }
              }
            }

            if (!p1.isAlive || !p2.isAlive) {
              continue;
            }
            if (now > p2.lastAttackTime + scene.getAttackCooldownMs(p2)) {
              p2.lastAttackTime = now;
              if (typeof p2.applyAttackStaminaCost === 'function') {
                p2.applyAttackStaminaCost(2);
              }
              // Clear charging flag after first attack
              if (p2.isCharging) p2.isCharging = false;
              const damage2 = p2.calculateMeleeDamage();
              const willDie = p1.health <= damage2;
              const actualDamage2 = p1.takeDamage(damage2, { critical: !!p2.lastAttackWasCritical });
              p2.damageDealt += actualDamage2;
              if (typeof p2.handleDamageDealtForUltimate === 'function') {
                p2.handleDamageDealtForUltimate(actualDamage2);
              }
              scene.createAttackEffect(p2, p1);
              if (scene.sfxManager) {
                scene.sfxManager.onAttack(p2);
                scene.sfxManager.onHit(p1, actualDamage2, willDie);
              }
              // Taunt triggers
              if (scene.tauntManager) {
                scene.tauntManager.triggerTaunt(p2, 'attack');
                scene.tauntManager.triggerTaunt(p2, 'hit');
                scene.tauntManager.triggerTaunt(p1, 'hurt');
              }
              if (!p1.isAlive) {
                //console.log('💀 Player killed:', p1.name, 'by', p2.name, '- Remaining alive:', scene.players.filter(p => p.isAlive).length - 1);
                p2.addElimination();
                scene.createDeathMarker(p1.sprite.x, p1.sprite.y, { popScale: p1.sprite.scale * 10 });
                if (scene.sfxManager) scene.sfxManager.onDeath(p1);
                // Taunt triggers for kill/death
                if (scene.tauntManager) {
                  scene.tauntManager.triggerTaunt(p2, 'killed');
                  scene.tauntManager.triggerTaunt(p1, 'death');
                }
              }
            }
          } else if (scene.rangedRange && dist < scene.rangedRange) {
            scene.tryShootArrow(p1, p2, dist);
            scene.tryShootArrow(p2, p1, dist);
          }
        }
      }
    }

    // Post-combat breathing
    if (scene.sfxManager) {
      scene.players.forEach(p => {
        if (p.prevBattling && !p.isBattling && p.isAlive) {
          scene.sfxManager.onBreath(p);
        }
      });
    }
  }

  checkWinCondition(scene) {
    // Don't check win condition if players are still spawning or game just started
    if (scene.playersSpawnInProgress || !scene.players || scene.players.length === 0) {
      return null;
    }

    // Fix time comparison - use consistent time system (Date.now() matches how matchStartTime is set)
    const timeSinceStart = Date.now() - (scene.matchStartTime || 0);
    if (timeSinceStart < 1000) { // Wait at least 1 second after match start
      return null;
    }

    const alivePlayers = scene.players.filter(p => p.isAlive);

    // Enhanced debug logging - check when there's 1 or 0 players left
    if (alivePlayers.length <= 1) {
      console.log('🔍 Win Condition Check:', {
        aliveCount: alivePlayers.length,
        totalPlayers: scene.players.length,
        gameState: scene.gameState,
        timeSinceStart: timeSinceStart,
        playersSpawnInProgress: scene.playersSpawnInProgress,
        alivePlayers: alivePlayers.map(p => ({
          name: p.name,
          health: p.health,
          maxHealth: p.maxHealth,
          isAlive: p.isAlive
        })),
        allPlayers: scene.players.map(p => ({
          name: p.name,
          health: p.health,
          isAlive: p.isAlive
        }))
      });
    }

    if (alivePlayers.length === 1 && scene.gameState === 'playing') {
      console.log('🎉 Match won by:', alivePlayers[0].name, 'eliminations:', alivePlayers[0].eliminations);
      return { winner: alivePlayers[0], type: 'single' };
    } else if (alivePlayers.length === 0 && scene.gameState === 'playing') {
      console.log('💀 Match ended with no winner - all players died');
      return { winner: null, type: 'draw' };
    }

    return null; // No winner yet
  }

  getPlayerTarget(player, allPlayers) {
    // Find nearest enemy (any other player)
    let nearestEnemy = null;
    let nearestDistance = Infinity;
    
    allPlayers.forEach(otherPlayer => {
      if (otherPlayer !== player && otherPlayer.isAlive) {
        const dist = Phaser.Math.Distance.Between(player.sprite.x, player.sprite.y, otherPlayer.sprite.x, otherPlayer.sprite.y);
        if (dist < nearestDistance) {
          nearestDistance = dist;
          nearestEnemy = otherPlayer;
        }
      }
    });
    
    return nearestEnemy;
  }
}
